/**
 * App shell: session, balance, and which screen is showing.
 *
 * Routing is a single piece of state rather than a router. The navigation is a
 * two-tab bar with screens one level deep beneath each; a router would add a
 * dependency and URL handling that a Telegram Mini App webview does not
 * meaningfully expose anyway.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, publicConfig, ApiError } from './api/client.ts';
import { errorMessage } from './api/errors.ts';
import type { BalanceView, UserView } from './api/types.ts';
import { Nav, type Tab } from './components/Nav.tsx';
import { Splash } from './components/Splash.tsx';
import { TermsGate } from './components/TermsGate.tsx';
import { ConsentDialog } from './components/ConsentDialog.tsx';
import { useI18n, useT, type Locale } from './i18n/index.tsx';
import { Lobby } from './screens/Lobby.tsx';
import { RoundScreen } from './screens/Round.tsx';
import { Wallet } from './screens/Wallet.tsx';
import { Deposit } from './screens/Deposit.tsx';
import { Withdraw } from './screens/Withdraw.tsx';
import { PastRounds } from './screens/PastRounds.tsx';
import { getInitData, startParam, useNativeBack } from './telegram.ts';

/** Each language's own name for itself — shown in the switcher regardless of the active locale. */
const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  am: 'አማርኛ',
  aa: 'Qafár afá',
  ti: 'ትግርኛ',
  so: 'Soomaali',
};

/**
 * The switcher only offers what actually has a real translation —
 * `aa`/`ti`/`so` are in `LOCALES` (so a Telegram-hinted player still lands
 * correctly, aliased to English) but have no native-speaker copy yet, and
 * offering them as if they were real choices would be a promise the app
 * doesn't keep. Add each to this list only once its own `catalogues` entry
 * in `i18n/messages.ts` stops aliasing to `en`.
 */
const SWITCHER_LOCALES: readonly Locale[] = ['en', 'am'];

type View =
  | { name: 'lobby' }
  | { name: 'round'; roundId: string }
  | { name: 'wallet' }
  | { name: 'deposit' }
  | { name: 'withdraw' }
  | { name: 'pastRounds' };

/** Which tab a view belongs to — so the bar stays lit on nested screens. */
function tabFor(view: View): Tab {
  return view.name === 'lobby' || view.name === 'round' ? 'play' : 'wallet';
}

/** Views that own the whole viewport and hide the tab bar. */
function isFullscreen(view: View): boolean {
  return view.name === 'round';
}

/**
 * The floor on how long the splash stays up. The session can resolve in a
 * few hundred ms on a good connection, and a brand frame that flashes by is
 * worse than none — this guarantees it gets one full pulse cycle.
 */
const MIN_SPLASH_MS = 1800;

/**
 * How often the open app re-reads the balance. Comfortably under the ~10s
 * deposit auto-approve delay, so a credit shows up while the player is still
 * looking at the screen that made them wait for it.
 */
const BALANCE_POLL_MS = 5_000;

export function App() {
  const t = useT();
  const { locale, setLocale } = useI18n();

  const [user, setUser] = useState<UserView | null>(null);
  const [balance, setBalance] = useState<BalanceView | null>(null);
  const [view, setView] = useState<View>({ name: 'lobby' });
  const [fatal, setFatal] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [supportUrl, setSupportUrl] = useState<string | null>(null);

  /**
   * The support contact is fetched before and independently of the session:
   * the state in which a player most needs it (account suspended, session
   * rejected) is exactly the state in which no authenticated call succeeds.
   */
  useEffect(() => {
    void publicConfig().then(({ supportUrl }) => setSupportUrl(supportUrl));
  }, []);

  /**
   * `POST /auth/session` is the first call: it verifies initData and registers
   * the player on first sight. Everything after it can assume a user row
   * exists, which is why no other screen handles "not registered yet".
   */
  useEffect(() => {
    if (!getInitData()) {
      setReady(true);
      return;
    }
    // Started alongside the session so the floor counts from the moment the
    // app began loading, not from when the session happened to resolve.
    const splashFloor = new Promise<void>((resolve) => window.setTimeout(resolve, MIN_SPLASH_MS));
    void api
      .session()
      .then((session) => {
        setUser(session.user);
        setBalance(session.balance);
      })
      .catch((err) => {
        // A suspended account fails here and must fail loudly — every
        // subsequent call would 403 and the app would look broken rather than
        // blocked.
        setFatal(errorMessage(err, t));
        if (err instanceof ApiError && err.status === 0) setFatal(null);
      })
      .finally(() => {
        // The splash is seen even when the session resolves early; a slow
        // session still hands over the moment it finishes (floor, not cap).
        void splashFloor.then(() => setReady(true));
      });
  }, [t]);

  /**
   * Pushes the app's resolved locale to the server whenever it changes — on
   * load (whatever `detectLocale()` picked: a stored choice, or Telegram's
   * hint) and on an explicit switch — so the bot's notifications follow the
   * same language, not a separate setting the player has to pick twice. Only
   * once a session exists: the endpoint is authenticated, and there is
   * nothing to sync before registration has happened.
   */
  useEffect(() => {
    if (!user) return;
    void api.setLanguage(locale).catch(() => {
      // Cosmetic: the bot falling back to the player's last-synced language
      // (or the server default) for one notification is not worth surfacing.
    });
  }, [user, locale]);

  const refreshBalance = useCallback(() => {
    void api
      .balance()
      .then(setBalance)
      .catch(() => {
        // Silent: a stale balance is a cosmetic problem, and an error banner
        // over a working game is not.
      });
  }, []);

  /**
   * Keep the balance fresh while the app is open.
   *
   * A Telebirr deposit is credited the moment its SMS matches the submitted
   * claim, but nothing pushes that to the client — the only socket this app
   * opens is scoped to a round, and a player topping up is not in one.
   * Without this poll the balance is only ever re-read on mount, so someone
   * who transfers money and then watches their wallet sees the old number
   * indefinitely and concludes their money vanished. That is the worst
   * possible moment to look broken, and it is the first thing every real
   * player does.
   *
   * Skipped while the webview is hidden (Telegram minimised, another chat on
   * top) — nobody is reading a stale number they cannot see. The same handler
   * runs on `visibilitychange`, so coming back to the app refreshes at once
   * rather than waiting out the remainder of an interval.
   */
  useEffect(() => {
    if (!user) return;
    const tick = () => {
      if (document.visibilityState === 'visible') refreshBalance();
    };
    const id = window.setInterval(tick, BALANCE_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user, refreshBalance]);

  /**
   * If this launch came from a bot deep link (`start_param`), land on the
   * screen the link promised. The bot sends `round_<id>` (round starting / you
   * won), `lobby` (announcement posters — the rounds list, for any game type),
   * and `wallet` (deposit approved). Applied once, after the session is
   * established — the round screen needs a user row to render, and a wallet
   * deep link is only worth honouring once the balance is real.
   */
  useEffect(() => {
    if (!ready || !user) return;
    const param = startParam();
    if (!param) return;
    if (param.startsWith('round_')) {
      const roundId = param.slice('round_'.length);
      if (roundId) setView({ name: 'round', roundId });
    } else if (param === 'wallet') {
      setView({ name: 'wallet' });
    } else if (param === 'lobby') {
      setView({ name: 'lobby' });
    }
  }, [ready, user]);

  const goBack = useCallback(() => {
    setView((current) => {
      switch (current.name) {
        case 'round':
          return { name: 'lobby' };
        case 'deposit':
        case 'withdraw':
        case 'pastRounds':
          return { name: 'wallet' };
        default:
          return current;
      }
    });
  }, []);

  const nested =
    view.name === 'round' ||
    view.name === 'deposit' ||
    view.name === 'withdraw' ||
    view.name === 'pastRounds';

  // Telegram's native back button, so the app behaves like the client around it.
  useNativeBack(nested ? goBack : null);

  if (!ready) {
    return <Splash />;
  }

  if (!getInitData()) {
    return (
      <div className="screen screen--centred gate">
        <h1 className="gate__title">{t.openInTelegram}</h1>
        <p className="gate__hint">{t.openInTelegramHint}</p>
      </div>
    );
  }

  if (fatal) {
    return (
      <div className="screen screen--centred gate">
        <h1 className="gate__title">{t.somethingWentWrong}</h1>
        <p className="gate__hint">{fatal}</p>
        {supportUrl && (
          <a className="gate__support" href={supportUrl} target="_blank" rel="noreferrer">
            {t.contactSupport}
          </a>
        )}
        <button type="button" className="button" onClick={() => window.location.reload()}>
          {t.retry}
        </button>
      </div>
    );
  }

  // Gate: T&C must be accepted before anything else is accessible.
  if (user && !user.termsAcceptedAt) {
    return (
      <TermsGate
        onAccepted={() => setUser((prev) => (prev ? { ...prev, termsAcceptedAt: new Date().toISOString() } : prev))}
      />
    );
  }

  // Gate: consent dialog shown once after T&C, overlaid on the app.
  const showConsent = user && !user.consentGivenAt;

  return (
    <div className="app">
      {view.name === 'lobby' && (
        <Lobby
          balance={balance}
          supportUrl={supportUrl}
          onOpenRound={(roundId) => setView({ name: 'round', roundId })}
          onRefreshBalance={refreshBalance}
        />
      )}

      {view.name === 'round' && (
        <RoundScreen
          roundId={view.roundId}
          myUserId={user?.id ?? null}
          onBack={goBack}
          onBalanceChanged={refreshBalance}
          onOpenRound={(roundId) => setView({ name: 'round', roundId })}
        />
      )}

      {view.name === 'wallet' && (
        <Wallet
          balance={balance}
          user={user}
          onDeposit={() => setView({ name: 'deposit' })}
          onWithdraw={() => setView({ name: 'withdraw' })}
          onPastRounds={() => setView({ name: 'pastRounds' })}
          onRefreshBalance={refreshBalance}
        />
      )}

      {view.name === 'pastRounds' && <PastRounds onBack={goBack} />}

      {view.name === 'deposit' && (
        <Deposit
          onBack={goBack}
          onDone={() => {
            // A submitted deposit credits nothing yet, but the wallet is where
            // its "awaiting review" status appears — which is the reassurance
            // the player is looking for right now.
            refreshBalance();
            setView({ name: 'wallet' });
          }}
        />
      )}

      {view.name === 'withdraw' && (
        <Withdraw
          onBack={goBack}
          onDone={() => {
            // This one *has* moved money — the funds are locked — so the
            // balance must be re-read before the wallet renders it.
            refreshBalance();
            setView({ name: 'wallet' });
          }}
        />
      )}

      {!isFullscreen(view) && (
        <>
          <Nav
            active={tabFor(view)}
            onChange={(tab) => setView(tab === 'play' ? { name: 'lobby' } : { name: 'wallet' })}
          />
          <select
            className="langtoggle langtoggle--select"
            value={locale}
            onChange={(e) => setLocale(e.target.value as Locale)}
            aria-label={t.language}
          >
            {SWITCHER_LOCALES.map((l) => (
              <option key={l} value={l}>
                {LOCALE_LABELS[l]}
              </option>
            ))}
          </select>
        </>
      )}
      {showConsent && (
        <ConsentDialog
          onDone={() => setUser((prev) => (prev ? { ...prev, consentGivenAt: new Date().toISOString() } : prev))}
        />
      )}
    </div>
  );
}
